import React from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import { useSelector } from 'react-redux';

import { Card } from 'components/Card';
import ExpandableSection from 'components/ExpandableSection';
import Message from 'components/Message';
import { bem } from 'helpers/class-name';
import Segment from 'helpers/segment';
import { selectActiveVenueId } from 'selectors/general';

import messages from './SmartRate.messages';
import { makeSmartRateFaqSectionId } from './SmartRateFaqs.helpers';
import { useSmartRateFaqOpenState } from './useSmartRateFaqOpenState';

const { element } = bem('smart-rate');

export const SMART_RATE_FAQ_LAYOUT = Object.freeze({
  CARD: 'card',
  EMBEDDED: 'embedded',
});

function FaqExpandableSection({ answer, faqId, question }) {
  const sectionId = makeSmartRateFaqSectionId(faqId);
  const [isOpen, setIsOpen] = useSmartRateFaqOpenState(sectionId);
  const venueId = useSelector(selectActiveVenueId);

  const handleToggle = next => {
    setIsOpen(next);
    if (next) {
      Segment.event('Click SmartRate FAQ', {
        faq_name: question,
        venue_id: venueId,
      });
    }
  };

  return (
    <ExpandableSection
      id={sectionId}
      isOpen={isOpen}
      onToggle={handleToggle}
      title={{ id: `smart-rate.faq.${faqId}.question`, defaultMessage: question }}>
      <ReactMarkdown className={element('faq-answer')}>{answer}</ReactMarkdown>
    </ExpandableSection>
  );
}

FaqExpandableSection.propTypes = {
  answer: PropTypes.string.isRequired,
  faqId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  question: PropTypes.string.isRequired,
};

function FaqContent({ faqs }) {
  return (
    <>
      <Message message={messages.faqTitle} as="h2" className={element('faq-title')} />
      {faqs.length > 0 && (
        <div className={element('faq-list')}>
          {faqs.map(({ id, question, answer }) => (
            <FaqExpandableSection key={id} answer={answer} faqId={id} question={question} />
          ))}
        </div>
      )}
    </>
  );
}
FaqContent.propTypes = {
  faqs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      question: PropTypes.string.isRequired,
      answer: PropTypes.string.isRequired,
    })
  ).isRequired,
};

const SmartRateFaqs = ({ faqs, isLoading, layout = SMART_RATE_FAQ_LAYOUT.CARD }) => {
  if (layout === SMART_RATE_FAQ_LAYOUT.EMBEDDED) {
    return (
      <section className={element('faq', { embedded: true })}>
        <FaqContent faqs={faqs} />
      </section>
    );
  }

  return (
    <Card className={element('faq-card')} loading={isLoading}>
      <FaqContent faqs={faqs} />
    </Card>
  );
};

SmartRateFaqs.propTypes = {
  faqs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      question: PropTypes.string.isRequired,
      answer: PropTypes.string.isRequired,
    })
  ).isRequired,
  isLoading: PropTypes.bool,
  layout: PropTypes.oneOf(Object.values(SMART_RATE_FAQ_LAYOUT)),
};

export default SmartRateFaqs;
